import React, { useState, useRef } from "react";
import "./contentpage.css";
import Question from "../components/Question";
import LandingSection from "./LandingSection";
import FloatingPageNav from "../components/FloatingPageNav";
import { Link } from "react-router-dom";

function IslamicTradePage17() {
  const contentRef = useRef(null);
     const links = {
    prev: "/islamic-trade-page-16", 
    index: "/index",                 
    next: "/islamic-trade-page-18", 
  };
  const titleText = "Financial Transactions by Proxy (Wikalah)";
  const questions = [
    {
      text: "1. What is the basic definition of Wikalah in Islamic jurisprudence?",
      options: [
        {
          value: "A",
          label: "A. A loan given without interest.",
        },
        {
          value: "B",
          label: "B. A contract of sale between two parties.",
        },
        {
          value: "C",
          label: "C. Appointing someone to act on one’s behalf.",
        },
        {
          value: "D",
          label: "D. A form of gift given without compensation.",
        },
      ],
      correct: "C",
    },
    {
      text: "2.  Which of the following is a valid example of Wikalah?",
      options: [
        { value: "A", label: "A. Giving a loan to a neighbor." },
        { value: "B", label: "B. Asking a friend to represent you in court." },
        { value: "C", label: "C. Selling a car directly to a buyer." },
        { value: "D", label: "D. Donating money to a charitys." },
      ],
      correct: "B",
    },
    {
      text: "3. Under which condition does an agent (wakeel) become liable for loss or damage?",
      options: [
        { value: "A", label: "A. If the principal does not pay him." },
        { value: "B", label: "B. If he exceeds his given authority." },
        { value: "C", label: "C. If he performs the task within limits." },
        { value: "D", label: "D. If the principal changes his mind later." },
      ],
      correct: "B",
    },
    {
      text: "4. How can a Wikalah contract be terminated?",
      options: [
        { value: "A", label: "A. Only through court order." },
        { value: "B", label: "B. Automatically after one week." },
        { value: "C", label: "C. By death of either party or by revocation." },
        { value: "D", label: "D. It cannot be terminated once signed." },
      ],
      correct: "C",
    },
  ];
  // State to track selected answers for all questions
  const [answers, setAnswers] = useState({});

  // Function to update an answer
  const handleAnswerChange = (questionIndex, selectedValue) => {
    setAnswers((prev) => ({ ...prev, [questionIndex]: selectedValue }));
  };

  // Compute score
  const score = questions.reduce((total, q, index) => {
    return total + (answers[index] === q.correct ? 1 : 0);
  }, 0);

  return (
    <div className="container" ref={contentRef}>
      <LandingSection
        titleText={titleText}
        scrollToRef={contentRef}
        progressValue={17}
        timerMinutes={60}
        currentLevel={1}
      />
     
      <p className="content">
        Welcome to <b>Day 17</b>, today we will explore the concept of Wikalah
        (Proxy) in Islamic financial transactions. Wikalah refers to appointing
        someone to act on your behalf in matters of trade, contracts, or
        representation. We will discuss its legal basis from the Quran and
        Sunnah, its conditions, types, and applications in everyday life and
        modern financial systems. Through this, you will understand how Islam
        facilitates ease and delegation in financial dealings while ensuring
        accountability and ethical conduct.
        <br />
        <br /> In the intricate fabric of Islamic financial transactions,
        Wikalah (agency by proxy) stands as a pivotal concept that facilitates
        commerce and alleviates practical challenges in trade and contractual
        dealings. Wikalah is the delegation of one’s authority to another person
        to act on their behalf in matters that are permissible in Islamic law,
        such as financial transactions, sales, or the management of assets. It
        reflects Islam’s adaptability to the dynamic needs of society while
        upholding the principles of justice, trust, and accountability.
        <br /> The Quran establishes the permissibility and wisdom of delegation
        in matters requiring expertise or representation. <br />
        Qur'an states: "Then send one of you with this silver coin of yours to
        the city; let him look to which is the best food and bring provision
        from it for you."{" "}
        <i>
          <a href="https://quran.com/18/19" target="blank">
            (Surah Al-Kahf, 18:19)
          </a>
        </i>
        <br /> This verse highlights the practical application of entrusting
        someone to carry out a task on behalf of a group. <br />
        Similarly, the Sunnah of the Prophet Muhammad (peace be upon him)
        further underscores the legitimacy of Wikalah. In a well-documented
        instance, the Prophet (peace be upon him) appointed agents to collect
        Zakah on his behalf, demonstrating the utility of delegation in
        fulfilling responsibilities for the greater good. The institution of
        Wikalah is not merely about convenience; it is deeply rooted in Islamic
        values. It promotes trust and collaboration while ensuring that all
        dealings are carried out within the boundaries of Shariah. <br />
        <br />
        <br />
      </p>
      <table className="trade-table">
        <tr>
          <th>The Proxy should posses four attributes </th>
        </tr>
        <tr>
          <td>He should be an adult</td>
        </tr>
        <tr>
          <td>He should be of sound Intellect</td>
        </tr>
        <tr>
          <td>He should be free and not a slave </td>
        </tr>
        <tr>
          <td>He should posses financial intelligence</td>
        </tr>
      </table>
      <p className="content">
        <h3>Ruling on Financial Transactions by Proxy : </h3>
        It is permissible(ja'iz) for the principal person to choose a proxy, and
        it is recommended(mustahab) for the proxy to comply.
        <br />
        <br />
        <b>Restriction on Delegating Authority to Another Proxy</b>
        <br />
        <br />
        It is not permissible for a proxy (wakil) to delegate their
        authority to another individual without the explicit consent of the
        principal (muwakkil). This restriction arises from the principle that
        the proxy is entrusted with a specific responsibility based on the trust
        and confidence of the principal. Since the wakil acts as an extension of
        the muwakkil’s will, transferring this authority to a third party
        without permission would breach the trust and undermine the original
        agreement. The Prophet Muhammad (peace be upon him) emphasized the
        importance of fulfilling trusts and responsibilities, stating: "When a
        man entrusts someone with something, let him deliver that trust back to
        its owner."
        <i>
          <a href="https://sunnah.com/abudawud:3535" target="blank">
            (Sunan Abi Dawood, 3534)
          </a>
        </i>
        <br />
        The only exception to this rule is when the nature of the task
        inherently requires delegation, or when there is an unforeseen
        necessity, and even then, the proxy must act within the boundaries of
        the muwakkil’s best interests. This ensures that the rights of the
        principal are safeguarded, and the integrity of the delegation is
        maintained in accordance with Shariah. <br />
        <br />
        <b>Adherence to the Principal's Stipulations by the Wakil</b>
        <br />
        <br />A wakil (proxy) must strictly adhere to the boundaries and
        conditions stipulated by the muwakkil (principal) when executing
        financial contracts or fulfilling delegated responsibilities. The wakil
        is entrusted to act within the framework outlined by the principal,
        ensuring that their actions reflect the intentions and interests of the
        muwakkil. Exceeding these prescribed limits is considered a breach of
        trust (khiyanah) and renders the wakil liable for any resulting harm or
        loss. The Quran emphasizes the importance of honoring trusts and
        agreements, stating: <br />
        "Indeed, Allah commands you to return trusts to their rightful owners;
        and when you judge between people, judge with fairness."
        <i>
          <a href="https://quran.com/4/58" target="blank">
            (Surah An-Nisa, 4:58)
          </a>
        </i>
        <br />
        Moreover, the Prophet Muhammad (peace be upon him) taught: "The hand of
        the agent is bound by the limits set by the one who has authorized him."
        (Sunan Al-Bayhaqi, 6:135). <br />
        By confining themselves to the conditions laid out by the principal, the
        wakil preserves the trust inherent in the agency relationship, ensures
        transparency, and fulfills their role in compliance with Islamic ethics
        and principles.
        <br />
        <br />
        <b>Termination of the Contract of Agency</b>
        <br />
        <br />
        In Islamic jurisprudence, both the wakil (proxy) and the muwakkil
        (principal) retain the right to annul the contract of agency (wikalah)
        at any time. This flexibility underscores the voluntary and trust-based
        nature of the agency relationship. However, this termination is not
        without boundaries; it must not lead to harm or injustice to either
        party. For instance, if the wikalah contract is revoked at a critical
        juncture where its annulment would result in financial loss or
        unfulfilled obligations, such termination becomes impermissible under
        Islamic principles. The Quran enjoins believers to avoid harm and act
        justly in all dealings.
        <br />
        Additionally, the Prophet Muhammad (peace be upon him) said: <br />
        “There should be neither harming nor reciprocating harm.”
        <i>
          <a href="https://sunnah.com/ibnmajah:2340" target="blank">
            (Sunan Ibn Majah, 2340)
          </a>
        </i>
        <br />
        This principle ensures that while both parties have the right to revoke
        the agency, it must be exercised responsibly, with due regard for the
        rights and well-being of the other party.
        <br />
        <br />
        <b>Termination of Agency Upon Death</b>
        <br />
        <br />
        The contract of wikalah (agency) is automatically annulled upon the
        death of either the wakil (proxy) or the muwakkil (principal), as the
        basis of the agreement i.e trust and mutual consent ceases to exist. If
        the wakil passes away, it is evident that he can no longer fulfill the
        responsibilities entrusted to him. Similarly, if the muwakkil dies, his
        wealth and affairs are transferred to his inheritors, and any prior
        agency agreement becomes void. This ensures that the inheritors' rights
        over the deceased's estate are preserved, and no one can act as a proxy
        without their explicit consent. The Quran emphasizes the rightful
        transfer of wealth upon death, stating:
        <br /> “For men there is a share in what their parents and close
        relatives leave, and for women there is a share in what their parents
        and close relatives leave whether it is little or much. ˹These are˺
        obligatory shares.”
        <i>
          <a href="https://quran.com/4/7" target="blank">
            (Surah An-Nisa, 4:7)
          </a>
        </i>
        <br />
        This principle safeguards the property of the deceased from unauthorized
        actions and ensures that the agency relationship does not continue in a
        manner that could compromise the rights of the rightful heirs. It aligns
        with the Islamic objective of preserving wealth and justice in financial
        transactions.
        <br />
        <br />
        <b>Adhering to Terms in Selling as a Proxy</b>
        <br />
        <br />
        When a wakil (proxy) is entrusted with selling an item on behalf of the
        muwakkil (principal), he must strictly adhere to the conditions
        stipulated or reasonably implied in the agreement. If no explicit
        consent is given for alternative arrangements, the wakil is obligated to
        sell the item for the local currency rather than bartering it for
        another item. Bartering could expose the muwakkil to risks or
        complications not intended when appointing the proxy. Moreover, the
        wakil must ensure that the payment is received immediately upon the sale
        unless the muwakkil has explicitly permitted a deferred payment
        arrangement. Delaying the payment without authorization may compromise
        the principal's interests, such as liquidity or potential financial
        security. <br />
        The Prophet Muhammad (peace be upon him) emphasized the importance of
        transparency and trust in transactions, saying: “The Muslim is bound by
        his conditions unless it involves something prohibited”
        <i>
          <a href="https://sunnah.com/abudawud:3594" target="blank">
            {" "}
            (Sunan Abi Dawood,5943)
          </a>
        </i>{" "}
        <br /> This guidance underscores that a wakil must operate within the
        boundaries set by the muwakkil, ensuring the transaction aligns with
        their intentions and avoids undue harm or risk.
        <br />
        <br />
        <b>Liability of the Proxy in Purchasing Defective Goods</b>
        <br />
        <br />
        When a wakil (proxy) is entrusted with purchasing an item on behalf of
        the muwakkil (principal) and knowingly buys a defective product without
        the muwakkil's consent, the wakil is liable for the consequences of this
        decision. Such an act breaches the trust placed in the wakil and
        violates the terms of the proxy arrangement, as it goes against the
        presumed intent of the muwakkil to acquire a product of acceptable
        quality. In this case, the wakil is obligated to rectify the situation
        by either purchasing a non-defective product that meets the required
        specifications or refunding the money to the muwakkil. This principle
        aligns with the Islamic emphasis on fulfilling trusts and ensuring
        fairness in financial dealings. <br />
        The Quran states: “Indeed, Allah commands you to render trusts to whom
        they are due and when you judge between people to judge with justice”
        <i>
          <a href="https://quran.com/4/58" target="blank">
            (Surah An-Nisa, 4:58)
          </a>
        </i>
        <br />
        By upholding this responsibility, the wakil ensures that the muwakkil's
        rights are protected and maintains the integrity of the proxy
        arrangement.
        <br />
        <br />
        <b>Summary of Today's Lesson</b>
        <br />
        <br />
        In today’s session, we explored the concept of Wikalah (Proxy) the
        Islamic framework for appointing someone to act on one’s behalf in
        financial or legal matters. We learned that Wikalah is a valid and
        flexible contract rooted in the Quran and Sunnah, facilitating ease in
        transactions and responsibilities. The session covered the essential
        conditions for a valid agency, the types of agency (restricted and
        unrestricted), and the rules governing compensation, liability, and
        termination of the contract. Real-life applications of Wikalah in trade,
        court representation, and modern financial services were discussed,
        highlighting its relevance in both traditional and contemporary Islamic
        economic practices.
        <br />
        <br />
        <b>Coming Up Next</b>
        <br />
        <br />
        In tomorrow’s session, we delve into the foundational principles of
        Sharikah i.e Islamic partnerships based on mutual cooperation, fairness,
        and shared responsibility. You will learn how Islamic law promotes
        collaborative economic activity while upholding justice, transparency,
        and ethical dealings between partners. We will explore various types of
        partnerships, such as Sharikat-ul-Inan, Muzara’ah, Mudarabah, and
        Musaqah, along with the specific conditions that govern each one. Join
        us as we examine the wisdom behind profit and loss sharing, the
        flexibility allowed in partnership arrangements, and the spiritual and
        economic values that make Sharikah a powerful model for equitable and
        faith-driven financial growth.
      </p>
      <h2>Test your understanding</h2>
      {questions.map((q, index) => (
        <Question
          key={index}
          questionText={q.text}
          options={q.options}
          correctAnswer={q.correct}
          onAnswer={(value) => handleAnswerChange(index, value)} // pass parent handler
        />
      ))}

      <div style={{ marginTop: "20px", fontWeight: "bold", fontSize: "1.2em" }}>
        Score: {score} / {questions.length}
      </div>

       
       <FloatingPageNav 
              prevLink={links.prev} 
              indexLink={links.index} 
              nextLink={links.next} 
            />
    </div>
  );
}

export default IslamicTradePage17;
